import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GetWine_wine, GetWine_wine_wineLists } from "../../../types/__generated__/GetWine";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import URL_PATTERNS from "../../../urls";

import {
  WineSpecsTitle,
  WineSpecsWrapper,
  WineSpecsRow,
  SpecTitle,
  SpecValue,
  CollectionLink,
  CollectionLinkWrapper,
} from "./style";

interface WineSpecsProps {
  wine: GetWine_wine | null | undefined;
  isWineDetailsPage: boolean;
  isSake: boolean;
}

const WineSpecs = ({ wine, isWineDetailsPage, isSake }: WineSpecsProps) => {
  const { t } = useTranslation([
    I18N_NAMESPACES.MY_BADGES,
    I18N_NAMESPACES.WINE_PAGES,
    I18N_NAMESPACES.COMMON,
  ]);

  interface WineSpecsRowProps {
    title: string;
    value: string | undefined | null;
  }

  const WineSpecRow = (wineSpecsRowProps: WineSpecsRowProps): ReactElement | null => {
    const { title, value } = wineSpecsRowProps || {};
    return !!title && !!value ? (
      <WineSpecsRow>
        <SpecTitle>{title}</SpecTitle>
        <SpecValue>{value}</SpecValue>
      </WineSpecsRow>
    ) : null;
  };

  const regionRow: ReactElement | null = (
    <WineSpecRow title={t("myBadges:region")} value={wine?.wineRegion?.name} />
  );

  const countryRow: ReactElement | null = (
    <WineSpecRow title={t("myBadges:country")} value={wine?.country?.name} />
  );

  const grapeRow: ReactElement | null = (
    <WineSpecRow
      title={isSake ? t("winePages:type") : t("myBadges:grapeType")}
      value={wine?.wineType?.name}
    />
  );

  const makerRow: ReactElement | null = (
    <WineSpecRow
      title={isSake ? t("winePages:producer") : t("winePages:wineMaker")}
      value={wine?.wineMaker?.name}
    />
  );

  const alcoholRow: ReactElement | null = (
    <WineSpecRow
      title={t("winePages:alcohol")}
      value={
        wine?.alcoholPercentage
          ? t("common:alcoholWithPercentage", {
              alcohol: wine?.alcoholPercentage,
            })
          : null
      }
    />
  );

  const vintageRow: ReactElement | null = <WineSpecRow title={t("winePages:vintage")} value={wine?.year} />;

  const cellarRow: ReactElement | null = (
    <WineSpecRow title={t("winePages:cellarPeriod")} value={wine?.wineCellarPeriod?.name} />
  );

  const closureRow: ReactElement | null = (
    <WineSpecRow title={t("winePages:closure")} value={wine?.bottleClosure?.name} />
  );

  const productionMethodRow: ReactElement | null = (
    <WineSpecRow title={t("common:productionMethod")} value={wine?.wineProductionMethods?.[0]?.name} />
  );

  const servingTempRow: ReactElement | null = (
    <WineSpecRow
      title={t("winePages:temperature")}
      value={
        wine?.wineServingTemperature
          ? t("winePages:temperatureWithCelsius", {
              name: wine.wineServingTemperature.name,
              min: wine.wineServingTemperature.minTemperature,
              max: wine.wineServingTemperature.maxTemperature,
            })
          : null
      }
    />
  );

  const stateRow: ReactElement | null = (
    <WineSpecRow title={t("winePages:state")} value={wine?.wineRegion?.state} />
  );

  const wineListLength = wine?.wineLists?.length ?? 0;
  const renderCollectionLink = (wineList: GetWine_wine_wineLists, index: number): ReactElement => (
    <CollectionLinkWrapper key={wineList?.slug}>
      <CollectionLink to={URL_PATTERNS.WINE_LIST(wineList?.slug)} target="_blank">
        {wineList?.name}
      </CollectionLink>
      {index < wineListLength - 1 && ","}&nbsp;
    </CollectionLinkWrapper>
  );

  const collectionsRow: ReactElement | null =
    (wine?.wineLists?.length ?? 0) > 0 ? (
      <WineSpecsRow>
        <SpecTitle>{t("common:collection")}</SpecTitle>
        <SpecValue>
          {wine?.wineLists?.map((wineList, index) => !!wineList && renderCollectionLink(wineList, index))}
        </SpecValue>
      </WineSpecsRow>
    ) : null;

  const renderContent = (): ReactElement => (
    <WineSpecsWrapper isWineDetailsPage={isWineDetailsPage}>
      <WineSpecsTitle isWineDetailsPage={isWineDetailsPage}>{t("winePages:specs")}</WineSpecsTitle>
      {regionRow}
      {countryRow}
      {grapeRow}
      {makerRow}
      {alcoholRow}
      {!isSake && vintageRow}
      {cellarRow}
      {closureRow}
      {productionMethodRow}
      {servingTempRow}
      {!isSake && stateRow}
      {collectionsRow}
    </WineSpecsWrapper>
  );

  return renderContent();
};

export default WineSpecs;
